'use client';

import React, { FC, useState } from 'react';
import { TypeGlobalNavFields } from '@/core/generated-types';
import { Image } from '@/components/ui';
import { DynamicComponent } from '@/components/common/DynamicComponent';
import { Cart } from '../Cart/Cart';
import UserMenu from './UserMenu/UserMenu';
import { GenericAccordion } from '../GenericAccordion/GenericAccordion';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { BlockImage } from '@/components/common';
import classNames from 'classnames';

export const SmallNav: FC<TypeGlobalNavFields & { isScrolled: boolean }> = ({
  logo,
  groups,
  drawerDirection = 'left',
  signInLink,
  linkMenuLoggedIn,
  i18n,
  smallDivider,
  isScrolled,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const translations = generateTranslationsObject(i18n?.fields);

  const handleMenuOpen = (isOpen: boolean) => {
    document.body.classList.add('overflow-hidden');
    setIsMenuOpen(isOpen);
  };

  const handleMenuClose = () => {
    setIsMenuClosing(true);
    setTimeout(() => {
      document.body.classList.remove('overflow-hidden');
      setIsMenuOpen(false);
      setIsMenuClosing(false);
    }, 200); // Match animation duration
  };

  return (
    <>
      {/* Nav bar */}
      <nav
        data-testid="global-nav"
        className={classNames(
          'bg-white md:hidden block px-2 sticky top-0 z-50 transition-shadow duration-300`',
          {
            'shadow-lg': isScrolled,
          },
        )}
      >
        <div className="grid grid-cols-[64px_auto_64px] mx-auto h-[64px] items-center">
          {/* Menu button */}
          <button
            aria-label="Menu"
            className="p-4"
            onClick={() => handleMenuOpen(!isMenuOpen)}
          >
            <svg
              width="24"
              height="22"
              viewBox="0 0 24 22"
              className="text-charcoal"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect x="0" y="0" width="24" height="2" fill="currentColor" />
              <rect x="0" y="9" width="24" height="2" fill="currentColor" />
              <rect x="0" y="20" width="24" height="2" fill="currentColor" />
            </svg>
          </button>

          {/* Logo */}
          {logo && (
            <div className="flex items-center justify-center">
              <Image
                imageData={logo}
                alt={logo.fields.altText}
                className="w-[166px]"
              />
            </div>
          )}

          {/* Cart */}
          <Cart />
        </div>
      </nav>

      {/* Menu */}
      {isMenuOpen && (
        <div
          className={`
            pb-8 bg-white border-t border-grey-mid-light md:hidden fixed top-0 z-50
            bottom-0 w-full transform transition-all duration-200 ease-in-out max-h-[100vh] overflow-y-auto
          ${
            drawerDirection === 'left'
              ? `left-0 ${isMenuClosing ? 'animate-slide-out-left' : 'animate-slide-in-left'}`
              : `right-0 ${isMenuClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`
          }`}
        >
          <div className="flex justify-end pb-4">
            <button
              aria-label="Close"
              onClick={handleMenuClose}
              className="p-5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
              >
                <path
                  d="M11 12.827L2.20416 21.6233C1.96402 21.8632 1.66219 21.986 1.29866 21.9918C0.935414 21.9973 0.628088 21.8744 0.37668 21.6233C0.12556 21.3719 0 21.0673 0 20.7096C0 20.3518 0.12556 20.0472 0.37668 19.7958L9.17295 11L0.37668 2.20416C0.13683 1.96402 0.0140154 1.66219 0.00823591 1.29866C0.00274537 0.935415 0.12556 0.628088 0.37668 0.37668C0.628088 0.12556 0.932669 0 1.29042 0C1.64817 0 1.95275 0.12556 2.20416 0.37668L11 9.17295L19.7958 0.37668C20.036 0.13683 20.3378 0.0140154 20.7013 0.00823591C21.0646 0.00274537 21.3719 0.12556 21.6233 0.37668C21.8744 0.628088 22 0.932669 22 1.29042C22 1.64817 21.8744 1.95275 21.6233 2.20416L12.827 11L21.6233 19.7958C21.8632 20.036 21.986 20.3378 21.9918 20.7013C21.9973 21.0646 21.8744 21.3719 21.6233 21.6233C21.3719 21.8744 21.0673 22 20.7096 22C20.3518 22 20.0472 21.8744 19.7958 21.6233L11 12.827Z"
                  fill="#424140"
                />
              </svg>
            </button>
          </div>
          <div className="">
            {groups?.map((group) => (
              <GenericAccordion
                key={group.sys.id}
                className="border-b border-transparent last:border-b-0 px-4"
                header={<span>{group.fields.title}</span>}
                headerClassName="w-full py-3 text-left flex items-center justify-between text-lg text-charcoal pr-1.5"
                transitionDuration={300}
                testId={`${group.sys.id}-accordion`}
              >
                <div className="pl-[21px] flex flex-col">
                  {group.fields.columns?.map((column) =>
                    column.fields.items?.map((item) => (
                      <DynamicComponent component={item} key={item.sys.id} />
                    )),
                  )}
                </div>
              </GenericAccordion>
            ))}

            <div className="my-10">
              {smallDivider && <BlockImage {...smallDivider.fields} />}
            </div>

            <UserMenu
              signInLink={signInLink}
              linkMenuLoggedIn={linkMenuLoggedIn}
              translations={translations}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(SmallNav);
